import { type LucideIcon } from 'lucide-react'
import { Button } from './button'
import { motion } from 'framer-motion'

interface EmptyStateProps {
  icon?: LucideIcon
  image?: string
  title: string
  description?: string
  actionLabel?: string
  onAction?: () => void
  secondaryLabel?: string
  onSecondary?: () => void
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xxl'
}

const sizes = {
  sm: { wrap: 'py-10', icon: 'w-10 h-10', iconInner: 'w-5 h-5', title: 'text-sm', desc: 'text-xs' },
  md: { wrap: 'py-16', icon: 'w-14 h-14', iconInner: 'w-7 h-7', title: 'text-base', desc: 'text-sm' },
  lg: { wrap: 'py-24', icon: 'w-20 h-20', iconInner: 'w-10 h-10', title: 'text-lg', desc: 'text-sm' },
  xxl: { wrap: 'py-32', icon: 'w-40 h-40', iconInner: 'w-10 h-10', title: 'text-lg', desc: 'text-sm' },

}

export function EmptyState({
  icon: Icon,
  image,
  title,
  description,
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
  className = '',
  size = 'md',
}: EmptyStateProps) {
  const s = sizes[size]
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className={`flex flex-col items-center justify-center text-center gap-4 w-full ${s.wrap} ${className}`}
    >
      {image ? (
        <motion.img
          src={image}
          alt={title}
          className={`${s.icon} object-contain`}
          animate={{
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ) : Icon ? (
        <div className={`${s.icon} rounded-2xl bg-muted/70 flex items-center justify-center`}>
          <Icon className={`${s.iconInner} text-muted-foreground`} />
        </div>
      ) : null}
      <div className="space-y-1.5 max-w-xs">
        <p className={`font-semibold text-foreground ${s.title}`}>{title}</p>
        {description && (
          <p className={`text-muted-foreground leading-relaxed ${s.desc}`}>{description}</p>
        )}
      </div>
      {(actionLabel || secondaryLabel) && (
        <div className="flex items-center gap-2 flex-wrap justify-center">
          {secondaryLabel && onSecondary && (
            <Button variant="outline" size="sm" onClick={onSecondary}>
              {secondaryLabel}
            </Button>
          )}
          {actionLabel && onAction && (
            <Button size="sm" onClick={onAction}>
              {actionLabel}
            </Button>
          )}
        </div>
      )}
    </motion.div>
  )
}
